import { useCallback, useEffect, useRef, useState } from 'react';

import type { TeleprompterSpeed, VideoMediaStrategy } from './videoRecorder.utils';
import type { VideoRecorderPhase } from './VideoRecorderPure';
import {
  browserVideoMediaStrategy,
  cameraErrorKind,
  pickVideoNoteMimeType,
  teleprompterDurationMs,
  VIDEO_NOTE_COUNTDOWN_SECONDS,
  VIDEO_NOTE_MAX_MS,
} from './videoRecorder.utils';
import { VideoRecorderPure } from './VideoRecorderPure';

export type VideoRecorderProps = {
  /** The script the rep reads — shown on the teleprompter. */
  script: string;
  /** Hand the finished take to the consumer (upload + send). Resolve to close; reject to keep the take for a retry. */
  onSend: (video: Blob, durationMs: number) => Promise<void>;
  /** Leave the recorder entirely; the camera is released first. */
  onCancel: () => void;
  /** How the camera stream is obtained. Defaults to `getUserMedia`; the extension can inject its own. */
  mediaStrategy?: VideoMediaStrategy;
  /** Open the camera as soon as the recorder mounts (the rep already asked to record). */
  autoOpen?: boolean;
  maxMs?: number;
  className?: string;
};

const TICK_MS = 100;

/**
 * Camera + MediaRecorder wiring for the video-note recorder. Renders {@link VideoRecorderPure}.
 * Recording time drives both the cap and the teleprompter, so pausing the take pauses the script.
 */
export function VideoRecorder({
  script,
  onSend,
  onCancel,
  mediaStrategy = browserVideoMediaStrategy,
  autoOpen = false,
  maxMs = VIDEO_NOTE_MAX_MS,
  className,
}: VideoRecorderProps) {
  const [phase, setPhase] = useState<VideoRecorderPhase>('idle');
  const [speed, setSpeed] = useState<TeleprompterSpeed>('normal');
  const [mirrored, setMirrored] = useState(true);
  const [countdown, setCountdown] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const takeRef = useRef<{ blob: Blob; durationMs: number } | null>(null);
  const elapsedRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const liveVideoRef = useRef<HTMLVideoElement | null>(null);
  const autoOpened = useRef(false);

  const clearTimers = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    timerRef.current = null;
    countdownRef.current = null;
  };

  const releaseCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (liveVideoRef.current) liveVideoRef.current.srcObject = null;
  }, []);

  const openCamera = useCallback(async () => {
    setErrorMessage(null);
    setPhase('starting');
    try {
      const stream = await mediaStrategy.getStream();
      streamRef.current = stream;
      if (liveVideoRef.current) liveVideoRef.current.srcObject = stream;
      setPhase('ready');
    } catch (error) {
      const kind = cameraErrorKind(error);
      if (kind === 'other') setErrorMessage(error instanceof Error ? error.message : null);
      setPhase(kind === 'other' ? 'error' : kind);
    }
  }, [mediaStrategy]);

  // Re-attach the stream when the live <video> remounts (e.g. after a retake).
  const attachLiveVideo = useCallback((el: HTMLVideoElement | null) => {
    liveVideoRef.current = el;
    if (el && streamRef.current) el.srcObject = streamRef.current;
  }, []);

  const stopRecording = useCallback(() => {
    clearTimers();
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    else setPhase(streamRef.current ? 'ready' : 'idle');
  }, []);

  const startRecording = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;
    const mimeType = pickVideoNoteMimeType((t) => MediaRecorder.isTypeSupported(t));
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    chunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || mimeType || 'video/webm' });
      takeRef.current = { blob, durationMs: elapsedRef.current };
      setPreviewUrl(URL.createObjectURL(blob));
      releaseCamera();
      setPhase('preview');
    };
    recorderRef.current = recorder;
    elapsedRef.current = 0;
    setElapsedMs(0);
    recorder.start(1000);
    setPhase('recording');
    timerRef.current = setInterval(() => {
      if (recorderRef.current?.state !== 'recording') return;
      elapsedRef.current += TICK_MS;
      setElapsedMs(elapsedRef.current);
      if (elapsedRef.current >= maxMs) stopRecording();
    }, TICK_MS);
  }, [maxMs, releaseCamera, stopRecording]);

  const beginCountdown = () => {
    let left = VIDEO_NOTE_COUNTDOWN_SECONDS;
    setCountdown(left);
    setPhase('countdown');
    countdownRef.current = setInterval(() => {
      left -= 1;
      if (left <= 0) {
        if (countdownRef.current) clearInterval(countdownRef.current);
        countdownRef.current = null;
        startRecording();
      } else {
        setCountdown(left);
      }
    }, 1000);
  };

  const togglePause = () => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    if (recorder.state === 'recording') {
      recorder.pause();
      setPhase('paused');
    } else if (recorder.state === 'paused') {
      recorder.resume();
      setPhase('recording');
    }
  };

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    takeRef.current = null;
    elapsedRef.current = 0;
    setElapsedMs(0);
    void openCamera();
  };

  const send = async () => {
    const take = takeRef.current;
    if (!take || phase === 'sending') return;
    setPhase('sending');
    try {
      await onSend(take.blob, take.durationMs);
    } catch {
      // The consumer shows its own error; keep the take so the rep can retry without re-recording.
      setPhase('preview');
    }
  };

  const cancel = () => {
    clearTimers();
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = null;
      recorder.stop();
    }
    releaseCamera();
    onCancel();
  };

  useEffect(() => {
    if (autoOpen && !autoOpened.current) {
      autoOpened.current = true;
      void openCamera();
    }
  }, [autoOpen, openCamera]);

  // Unmount: never leave the camera light on.
  useEffect(
    () => () => {
      clearTimers();
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.onstop = null;
        recorder.stop();
      }
      streamRef.current?.getTracks().forEach((track) => track.stop());
    },
    [],
  );
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const teleprompterProgress = phase === 'recording' || phase === 'paused' ? elapsedMs / teleprompterDurationMs(script, speed) : 0;

  return (
    <VideoRecorderPure
      phase={phase}
      script={script}
      speed={speed}
      mirrored={mirrored}
      countdown={countdown}
      elapsedMs={elapsedMs}
      maxMs={maxMs}
      teleprompterProgress={teleprompterProgress}
      previewUrl={previewUrl}
      errorMessage={errorMessage}
      liveVideoRef={attachLiveVideo}
      onOpenCamera={() => void openCamera()}
      onRecord={beginCountdown}
      onStop={stopRecording}
      onPauseToggle={togglePause}
      onRetake={retake}
      onSend={() => void send()}
      onCancel={cancel}
      onSpeedChange={setSpeed}
      onMirrorToggle={() => setMirrored((m) => !m)}
      className={className}
    />
  );
}
